/**
 * 파일명 : evl0102m.tsx
 * 설명 :  업무서약서 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0       2026-01-08   변재석     최초작성
 * 1.1       2026-02-02   변재석     model/slice 분리 (타입, 상수, 유틸 함수)
 * 1.2       2026-02-03   변재석     PK 상수제거
 */

/************************************************
 * Import 영역
 ************************************************/
/*  React  */
import React, { useState, useEffect, useRef, useCallback } from "react";

/*  AntD  */
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Divider,
  DatePicker,
  message,
  Descriptions,
  Modal,
  Select,
  Spin,
} from "antd";
import { PlusOutlined, ExpandOutlined, CompressOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

/*  업무  */
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

/* Reducer - 타입 정의 */
import type {
  ITaskRow,
  IEtcRow,
  IUserInfo,
  IPropsEvl0102M,
  IGrdCdOption,
} from "../../../redux/models/evl/evl0102m_model";

/* Reducer - 상수 및 유틸 함수 */
import {
  DTL_TYP_CD,
  createDefaultUserInfo,
  createDefaultTaskRow,
  createDefaultTeamContribution,
  createDefaultSelfDevelopment,
  parsePeriodFromDates,
  formatDateToYYYYMMDD,
  calculateTotalWeight,
  disabledFutureDate,
  parseTeamContributionFromResponse,
  parseSelfDevelopmentFromResponse,
} from "../../../redux/slice/evl/evl0102m_slice";

const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * 실시간 글자수 카운트를 지원하는 TextArea 래퍼 컴포넌트
 * - 내부 state로 카운트 관리 (테이블 리렌더 영향 없음)
 * - useRef로 최신값 유지 → onBlur 시 안정적으로 전달
 * - 키보드 연속 입력 오류 없음
 */
const CountTextArea: React.FC<{
  rowKey: string;
  rows?: number;
  defaultValue?: string;
  maxLength: number;
  onSave: (value: string) => void;
  disabled?: boolean;
  expanded?: boolean;
}> = React.memo(({ rowKey, rows = 3, defaultValue = "", maxLength, onSave, disabled, expanded = false }) => {
  const [count, setCount] = useState(defaultValue.length);
  const valueRef = useRef(defaultValue);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    valueRef.current = val;
    setCount(val.length);
  }, []);

  const handleBlur = useCallback(() => {
    onSave(valueRef.current);
  }, [onSave]);

  return (
    <div style={{ position: 'relative' }}>
      <TextArea
        key={`${rowKey}-${expanded}`}
        rows={expanded ? undefined : rows}
        autoSize={expanded ? { minRows: rows } : false}
        defaultValue={defaultValue}
        maxLength={maxLength}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        style={{ paddingBottom: 22 }}
      />
      <span style={{
        position: 'absolute',
        right: 12,
        bottom: 6,
        fontSize: 11,
        color: '#bbb',
        pointerEvents: 'none',
        lineHeight: 1,
      }}>
        {count} / {maxLength}
      </span>
    </div>
  );
});

// 타입 별칭 (기존 코드 호환성을 위해)
type TaskRow = ITaskRow;
type EtcRow = IEtcRow;
type UserInfo = IUserInfo;
type Evl0102mProps = IPropsEvl0102M;

/**
 * 컴포넌트명 : Evl0102m
 * 기능 : 업무서약서 작성 화면 메인 컴포넌트
 * 설명 : 본인 정보, 담당 직무(동적 추가/삭제), 기타 서약, 자기계발 부문으로 구성
 *        담당 직무의 비중 합계가 100%일 때만 제출 가능
 **/
const Evl0102m: React.FC<Evl0102mProps> = (props) => {
  // 평가년도 (props에서 받거나 현재 연도)
  const currentYear = new Date().getFullYear().toString();

  // props에서 값 추출 (props 우선, 없으면 세션/기본값 사용)
  const empNo = props.empNo || common.getEmpNo();
  const evlYear = props.evlYear || currentYear;
  const evlEnableYn = props.evlEnableYn || "Y";
  
  // ============= pledgeEndDate 제거 이유 =============
  // 보안상 이유로 서버에서 계산한 bizPlgModEdDt, isWithinDeadline 값을 우선으로 사용.
  // Props에서 받은 초기값은 클라이언트 시간 조작 공격에 취약하므로 제거.
  // 모든 마감일 검증은 API 응답값(bizPlgModEdDt, isWithinDeadline)과 
  // 백엔드 재검증(createEvlPledge 메서드)으로 처리.
  // const pledgeEndDate = props.pledgeEndDate || "20260306";

  // 미리보기 모드 (evl0104m 항목관리에서 양식 확인용)
  const isPreview = props.viewMode === "preview";

  // 읽기 전용 모드 (evl0103m 최종평가결과에서 조회용)
  const isReadonly =
    props.viewMode === "readonly" || props.empNo !== common.getEmpNo();

  // 데이터 로딩 상태 (깜빡임 방지)
  const [isDataLoading, setIsDataLoading] = useState(true);

  // 제출 여부 (DB에서 조회)
  const [bizPlgSubYn, setBizPlgSubYn] = useState("N");

  // 서버 기반 마감일 정보 (API 응답으로부터 받음)
  const [bizPlgModEdDt, setBizPlgModEdDt] = useState<string>("");
  const [isWithinDeadlineFromServer, setIsWithinDeadlineFromServer] = useState<boolean>(false);

  // 접근 차단 상태 (props 없이 직접 접근 시 화면 렌더링 방지)
  const [accessBlocked, setAccessBlocked] = useState(false);

  // TextArea 전체 펼치기/접기 상태
  const [textAreaExpanded, setTextAreaExpanded] = useState(false);

  // 수정 가능 여부 (evlEnableYn === 'Y' 이고 bizPlgSubYn !== 'Y' 일 때만 저장/제출 가능, 미리보기/읽기전용 시 불가)
  const isEditable = !isPreview && !isReadonly && evlEnableYn === "Y" && bizPlgSubYn !== "Y";

  // 마감일 이내 여부: 서버 기반 검증 값 사용
  // (클라이언트 시간 조작 방지를 위해 서버에서 계산한 값만 사용)
  const isWithinDeadline = isWithinDeadlineFromServer;

  // 제출취소 가능 여부 (이미 제출됨 + 기한 내 + 미리보기 아닐 것)
  // evlEnableYn 조건 제외: 평가 활성 여부와 무관하게 마감일 내 제출취소 허용
  const isCancelable = !isPreview && bizPlgSubYn === "Y" && isWithinDeadline;

  // 직군코드 (064 코드그룹)
  const [grdCd, setGrdCd] = useState("");
  const [grdCdOptions, setGrdCdOptions] = useState<
    { cd: string; cdNm: string }[]
  >([]);

  /**
   * 기능 : userInfo state
   * 설명 : DB에서 받아온 본인 정보를 관리하는 상태 (읽기 전용)
   **/
  const [userInfo, setUserInfo] = useState<UserInfo>(createDefaultUserInfo());

  /**
   * 기능 : pledgePeriod state
   * 설명 : 업무서약기간 (평가년도 기준 자동 세팅)
   **/
  const [pledgePeriod, setPledgePeriod] = useState<[any, any]>([
    dayjs(`${evlYear}-01-01`),
    dayjs(`${evlYear}-12-31`),
  ]);

  /**
   * 기능 : currentGradeStartDate state
   * 설명 : 현직급시작일자
   **/
  const [currentGradeStartDate, setCurrentGradeStartDate] = useState<any>(null);

  /**
   * 기능 : taskHrEvlItmId state
   * 설명 : 담당직무 항목의 hrEvlItmId (TEVL003에서 조회)
   *        새 업무 추가 시 이 값을 사용
   **/
  const [taskHrEvlItmId, setTaskHrEvlItmId] = useState<string>("");

  /**
   * 기능 : tasks state
   * 설명 : 담당 직무 목록을 관리하는 상태
   *        각 task는 hrEvlItmId, sno, name(업무명), goal(업무목표), weight(비중), period(기간) 속성을 가짐
   **/
  const [tasks, setTasks] = useState<TaskRow[]>([
    createDefaultTaskRow(1, evlYear),
  ]);

  /**
   * 기능 : nextId state
   * 설명 : 새로운 업무를 추가할 때 사용할 고유 ID 값을 관리
   **/
  const [nextId, setNextId] = useState(2);

  /**
   * 기능 : teamContribution state
   * 설명 : 팀기여 부문 데이터 관리
   **/
  const [teamContribution, setTeamContribution] = useState<EtcRow[]>(
    createDefaultTeamContribution(evlYear),
  );

  /**
   * 기능 : selfDevelopment state
   * 설명 : 자기개발 부문 데이터 관리
   **/
  const [selfDevelopment, setSelfDevelopment] = useState<EtcRow[]>(
    createDefaultSelfDevelopment(evlYear),
  );

  /**
   * 기능 : messageApi
   * 설명 : Ant Design의 message 컴포넌트를 사용하여 알림 메시지를 표시하기 위한 API
   **/
  const [messageApi, contextHolder] = message.useMessage();

  // 빠른 연속 입력 시 setState 조절용
  const rafIdRef = useRef<number>(0);

  // Modal 관리용 ref
  const confirmModalRefs = useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  // 검증 실패 시 스크롤 이동용 ref들
  const grdSelectRef = useRef<any>(null);
  const currentGradeStartDateRef = useRef<HTMLDivElement>(null);
  const pledgePeriodRef = useRef<HTMLDivElement>(null);
  const tasksTableRef = useRef<HTMLDivElement>(null);
  const teamContributionTableRef = useRef<HTMLDivElement>(null);
  const selfDevelopmentTableRef = useRef<HTMLDivElement>(null);

  /**
   * 기능 : useEffect - 화면 접근 권한 체크
   * 설명 : props로 empNo, evlYear가 전달되지 않으면 접근 차단
   *        (인사평가 메인, 최종평가결과 페이지에서만 접근 가능)
   **/
  useEffect(() => {
    // 미리보기 모드에서는 접근 체크 생략 (빈 양식만 표시)
    if (isPreview) return;

    // props로 empNo, evlYear가 전달되지 않은 경우 (직접 접근 시)
    if (!props.empNo || !props.evlYear) {
      setAccessBlocked(true);
      openConfirmModal({
        title: "인사평가 메인페이지에서 접근하십시오.",
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
        onOk: () => {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
        onCancel: () => {
          // ESC 또는 모달 취소 시에도 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
      });
    }
  }, [isPreview, props.empNo, props.evlYear]);

  /**
   * 기능 : useEffect - 브라우저 뒤로가기 시 Modal 닫기
   * 설명 : popstate 이벤트 발생 시 열려있는 모든 Modal 제거
   **/
  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  /**
   * 기능 : useEffect - 컴포넌트 마운트 시 직군명 조회
   * 설명 : 컴포넌트 마운트 시 코드그룹 '064'에서 직군명을 조회해 grdNm 업데이트
   **/
  useEffect(() => {
    // 미리보기 모드이거나 props가 없으면 조회하지 않음
    if (isPreview || !props.empNo || !props.evlYear) return;

    const option = {
      useYn: "",
      callback: (codeItems: any[]) => {
        // Select 옵션용 저장만 수행 (초기값 설정 안함)
        if (codeItems && Array.isArray(codeItems)) {
          setGrdCdOptions(
            codeItems.map((item: any) => ({ cd: item.cd, cdNm: item.cdNm })),
          );
        }
      },
    };
    common.getComCodeList("064", option);
  }, [isPreview, props.empNo, props.evlYear]);

  /**
   * 기능 : loadPledgeData
   * 설명 : 업무서약서 조회 API 호출
   **/
  const loadPledgeData = (silent?: boolean) => {
    // 로딩 시작 (silent mode가 아닐 때만: silent는 배경 재조회용)
    if (!silent) {
      setIsDataLoading(true);
    }

    const requestData: any = {
      empNo: empNo,
      evlYear: evlYear,
    };

    // 미리보기 모드: previewYn 플래그 전달 → 서버에서 TEVL003 항목 마스터만 조회
    if (isPreview) {
      requestData.previewYn = 'Y';
    }

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0012", // 업무서약서 조회
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const list = data.list || [];

        // 미리보기 모드: header 없이 항목 구조만 처리
        if (!isPreview) {
          const header = data.header || {};

          // 1. 본인 정보 설정 (grdNm은 마운트 시 064로 조회됨)
          setUserInfo((prev) => ({
            ...prev,
            userNm: header.userNm || "",
            postNm: header.postNm || "",
            dutyNm: header.dutyNm || "",
            upperDeptNm: header.upperDeptNm || "",
            deptNm: header.deptNm || "",
          }));

          // 2. TEVL010에서 받아온 본인정보 데이터 처리
          // 제출 여부
          setBizPlgSubYn(header.bizPlgSubYn || "N");

          // 서버 기반 마감일 정보 (보안: 클라이언트 시간 조작 방지)
          // data 최상위 레벨에서 직접 추출 (API 응답 구조)
          setBizPlgModEdDt(data.bizPlgModEdDt || "");
          setIsWithinDeadlineFromServer(data.isWithinDeadline === true);

          // 직군코드 (DB에서 받아온 값이 있으면 사용, 없으면 유지)
          if (header.grdCd) {
            setGrdCd(header.grdCd);
            // grdNm도 업데이트
            const grdMap = new Map(grdCdOptions.map((opt) => [opt.cd, opt.cdNm]));
            setUserInfo((prev) => ({
              ...prev,
              grdNm: grdMap.get(header.grdCd) || prev.grdNm,
            }));
          } else {
            // 값이 없으면 빈 상태 유지
            setGrdCd("");
            setUserInfo((prev) => ({
              ...prev,
              grdNm: "",
            }));
          }

          // 현직급시작일자 (YYYYMMDD 형식)
          if (header.currPosStDt) {
            const currDate = String(header.currPosStDt).trim();
            if (/^\d{8}$/.test(currDate)) {
              setCurrentGradeStartDate(dayjs(currDate, "YYYYMMDD"));
            }
          }

          // 업무서약기간 (bizPlgStDt, bizPlgEdDt)
          if (header.bizPlgStDt && header.bizPlgEdDt) {
            const stDt = String(header.bizPlgStDt).trim();
            const edDt = String(header.bizPlgEdDt).trim();
            if (/^\d{8}$/.test(stDt) && /^\d{8}$/.test(edDt)) {
              setPledgePeriod([dayjs(stDt, "YYYYMMDD"), dayjs(edDt, "YYYYMMDD")]);
            }
          } else {
            // 값이 없으면 평가년도 기본값 세팅
            setPledgePeriod([
              dayjs(`${evlYear}-01-01`),
              dayjs(`${evlYear}-12-31`),
            ]);
          }
        }

        // 3. DTL_TYP_CD별로 데이터 분류 (동적으로 처리)
        const taskList: TaskRow[] = [];
        let taskId = 1;

        // 기본 기간 (평가년도 기준)
        const defaultPeriod: [any, any] = [
          dayjs(`${evlYear}-01-01`),
          dayjs(`${evlYear}-12-31`),
        ];

        // 담당직무 항목의 hrEvlItmId 찾기 (TEVL003에서 조회된 값)
        const taskItem = list.find(
          (item: any) => item.dtlTypCd === DTL_TYP_CD.TASK,
        );
        const taskHrEvlItmId = taskItem?.hrEvlItmId || "";

        // 담당직무 (dtlTypCd === '24') 처리
        list.forEach((item: any) => {
          if (item.dtlTypCd === DTL_TYP_CD.TASK && item.sno) {
            // sno가 있는 경우만 (실제 저장된 데이터)
            taskList.push({
              id: taskId++,
              hrEvlItmId: item.hrEvlItmId,
              sno: item.sno,
              name: item.taskNm || "",
              goal: item.goalCntn || "",
              weight: item.weightRt ? item.weightRt.toString() : "",
              period:
                parsePeriodFromDates(item.bizPlgStDt, item.bizPlgEdDt) ||
                defaultPeriod,
            });
          }
        });

        // 담당직무 hrEvlItmId 저장 (새 업무 추가 시 사용)
        setTaskHrEvlItmId(taskHrEvlItmId);

        // 담당 직무가 없으면 빈 항목 1개 추가 (hrEvlItmId는 TEVL003에서 가져온 값 사용)
        if (taskList.length === 0) {
          taskList.push({
            id: 1,
            hrEvlItmId: taskHrEvlItmId,
            name: "",
            goal: "",
            weight: "",
            period: defaultPeriod,
          });
        }

        // 팀기여 (dtlTypCd === '22') - slice 함수 사용하여 동적 처리
        const teamContribList = parseTeamContributionFromResponse(
          list,
          evlYear,
        );

        // 자기개발 (dtlTypCd === '23') - slice 함수 사용하여 동적 처리
        const selfDevList = parseSelfDevelopmentFromResponse(list, evlYear);

        setTasks(taskList);
        setNextId(taskList.length + 1);
        setTeamContribution(teamContribList);
        setSelfDevelopment(selfDevList);

        // 로딩 완료
        setIsDataLoading(false);

        if (!isPreview && !silent) {
          messageApi.success("조회되었습니다.");
        }
      },
      error: (errorMsg: string) => {
        if (!isPreview) {
          messageApi.error(errorMsg || "업무서약서 조회에 실패했습니다.");
        }
      },
    });
  };

  /**
   * 기능 : useEffect
   * 설명 : empNo, evlYear가 변경될 때 조회 API 호출
   *        props로 전달된 값이 있을 때만 조회
   **/
  useEffect(() => {
    // 미리보기 모드: 로그인 사용자 기준으로 항목 구조만 조회
    if (isPreview) {
      loadPledgeData();
      return;
    }

    // props로 전달된 값이 있을 때만 조회
    if (props.empNo && props.evlYear && empNo && evlYear) {
      loadPledgeData();
    }
  }, [isPreview, props.empNo, props.evlYear, empNo, evlYear]); // 관련 값 변경 시 재실행

  /**
   * 기능 : updateTask
   * 설명 : 특정 업무의 특정 필드 값을 업데이트
   * param :
   *   - id: 업데이트할 업무의 고유 ID
   *   - field: 업데이트할 필드명 (name, goal, weight 등)
   *   - value: 새로운 값
   * return : None
   **/
  const updateTask = (id: number, field: keyof TaskRow, value: string) => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, [field]: value } : t)),
      );
    });
  };

  /**
   * 기능 : addTask
   * 설명 : 새로운 빈 업무를 목록에 추가
   * param : None
   * return : None
   **/
  const addTask = () => {
    const newTask = createDefaultTaskRow(nextId, evlYear);
    newTask.hrEvlItmId = taskHrEvlItmId; // TEVL003에서 가져온 담당직무 항목ID 사용
    setTasks([...tasks, newTask]);
    setNextId(nextId + 1);
  };

  /**
   * 기능 : removeTask
   * 설명 : 특정 업무를 목록에서 삭제 (최소 1개는 유지)
   * param : id - 삭제할 업무의 고유 ID
   * return : None
   **/
  const removeTask = (id: number) => {
    if (tasks.length > 1) {
      setTasks(tasks.filter((t) => t.id !== id));
    }
  };

  /**
   * 기능 : getTotalWeight
   * 설명 : 모든 업무의 비중 합계를 계산 (slice의 calculateTotalWeight 사용)
   * param : None
   * return : 비중 합계 (숫자)
   **/
  const getTotalWeight = () => calculateTotalWeight(tasks);

  /**
   * 기능 : handleSubmit
   * 설명 : 제출 버튼 클릭 시 실행되는 함수
   *        모든 필수 입력값 검증 및 비중 합계 100% 확인 후 제출
   * param : None
   * return : None
   **/
  const handleSubmit = () => {
    // 스크롤 이동 헬퍼 함수
    const scrollAndError = (
      ref: React.RefObject<HTMLElement>,
      errorMsg: string,
    ) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
          ref.current.focus?.();
        }
      }, 300);
    };

    // task index로 스크롤하는 헬퍼 함수
    const scrollAndErrorByTaskIndex = (taskIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-task-index="${taskIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    };

    // 팀기여 index로 스크롤하는 헬퍼 함수
    const scrollAndErrorByTeamIndex = (teamIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-team-index="${teamIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    };

    // 자기개발 index로 스크롤하는 헬퍼 함수
    const scrollAndErrorBySelfIndex = (selfIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-self-index="${selfIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    };

    // 0. 직군 검증
    if (!grdCd) {
      scrollAndError(grdSelectRef, "직군을 선택해주세요.");
      return;
    }

    // 1. 현직급시작일자 검증
    if (!currentGradeStartDate) {
      scrollAndError(
        currentGradeStartDateRef,
        "현직급시작일자를 입력해주세요.",
      );
      return;
    }

    // 2. 업무서약기간 검증
    if (!pledgePeriod || !pledgePeriod[0] || !pledgePeriod[1]) {
      scrollAndError(pledgePeriodRef, "업무서약기간을 입력해주세요.");
      return;
    }

    // 2-1. 업무서약기간 날짜 유효성 검증
    if (dayjs(pledgePeriod[0]).isAfter(dayjs(pledgePeriod[1]))) {
      scrollAndError(
        pledgePeriodRef,
        "업무서약기간 - 시작일이 종료일보다 클 수 없습니다.",
      );
      return;
    }

    // 3. 담당 직무 검증
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      if (!task.name.trim()) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 업무명을 입력해주세요.`);
        return;
      }

      if (!task.weight || parseFloat(task.weight) <= 0) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 비중을 입력해주세요.`);
        return;
      }

      if (!task.period || !task.period[0] || !task.period[1]) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 기간을 입력해주세요.`);
        return;
      }

      // 담당 직무 기간 날짜 유효성 검증
      if (dayjs(task.period[0]).isAfter(dayjs(task.period[1]))) {
        scrollAndErrorByTaskIndex(
          i,
          `업무 ${i + 1} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }

      if (!task.goal.trim()) {
        scrollAndErrorByTaskIndex(
          i,
          `업무 ${i + 1}의 업무 목표를 입력해주세요.`,
        );
        return;
      }

      if (task.goal.length > 500) {
        scrollAndErrorByTaskIndex(
          i,
          `업무 ${i + 1}의 업무 목표는 500자 이하로 입력해주세요. (현재 ${task.goal.length}자)`,
        );
        return;
      }
    }

    // 4. 비중 합계 검증
    if (getTotalWeight() !== 100) {
      scrollAndErrorByTaskIndex(
        0,
        "업무 비중의 합계는 100%여야 제출할 수 있습니다.",
      );
      return;
    }

    // 5. 팀기여 부문 검증
    for (let ti = 0; ti < teamContribution.length; ti++) {
      const item = teamContribution[ti];
      if (!item.content.trim()) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 내용을 입력해주세요.`,
        );
        return;
      }
      if (item.content.length > 500) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
      if (!item.period || !item.period[0] || !item.period[1]) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 기간을 입력해주세요.`,
        );
        return;
      }
      // 팀기여 부문 기간 날짜 유효성 검증
      if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }
    }

    // 6. 자기개발 부문 검증
    for (let si = 0; si < selfDevelopment.length; si++) {
      const item = selfDevelopment[si];
      if (!item.content.trim()) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 내용을 입력해주세요.`,
        );
        return;
      }
      if (item.content.length > 500) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
      if (!item.period || !item.period[0] || !item.period[1]) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 기간을 입력해주세요.`,
        );
        return;
      }
      // 자기개발 부문 기간 날짜 유효성 검증
      if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }
    }

    // 제출 확인 Modal
    openConfirmModal({
      title: "제출하시겠습니까?",
      content: "제출 시, 더 이상 수정할 수 없으니 주의하시기 바랍니다",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performSubmit();
      },
    });
  };

  /**
   * 기능 : performSubmit
   * 설명 : 실제 제출 로직 실행 (submitYn='Y'로 저장)
   **/
  const performSubmit = () => {
    // 날짜 형식 변환 함수 (dayjs -> YYYYMMDD)
    const formatDate = (date: any): string => {
      if (!date) return "";
      return dayjs(date).format("YYYYMMDD");
    };

    // 날짜 범위 분해 ([dayjs, dayjs] -> [YYYYMMDD, YYYYMMDD])
    const splitPeriod = (period: [any, any] | null): [string, string] => {
      if (!period || !period[0] || !period[1]) return ["", ""];
      return [
        dayjs(period[0]).format("YYYYMMDD"),
        dayjs(period[1]).format("YYYYMMDD"),
      ];
    };

    // SNO를 2자리 문자열로 변환 (01, 02, 03...)
    const formatSno = (num: number): string => {
      return num.toString().padStart(2, "0");
    };

    // DTO 구조에 맞게 list 생성
    const list: any[] = [];

    // 담당 직무 추가 (sno: 01, 02, 03...) - 동적 hrEvlItmId 사용
    tasks.forEach((task, index) => {
      const [stDt, edDt] = splitPeriod(task.period);
      list.push({
        hrEvlItmId: task.hrEvlItmId || "",
        sno: formatSno(index + 1),
        taskNm: task.name,
        goalCntn: task.goal,
        weightRt: task.weight ? parseInt(task.weight) : 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 팀기여 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq에서 가져옴)
    teamContribution.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      // SNO: itmSeq 우선, 없으면 item.sno 사용
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 자기개발 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq에서 가져옴)
    selfDevelopment.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      // SNO: itmSeq 우선, 없으면 item.sno 사용
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 요청 데이터 생성 (제출 시 bizPlgSubYn='Y')
    const requestData = {
      empNo: empNo, // props에서 받은 값 사용
      evlYear: evlYear,
      // === TEVL010 (본인정보) - root 레벨 ===
      grdCd: grdCd,
      currPosStDt: formatDate(currentGradeStartDate),
      bizPlgStDt: splitPeriod(pledgePeriod)[0],
      bizPlgEdDt: splitPeriod(pledgePeriod)[1],
      bizPlgSubYn: "Y",
      // === TEVL009 (상세) ===
      list: list,
    };

    // API 호출
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장 (저장과 제출 공통 사용)
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const resultCode = data.resultCode;
        const resultMsg = data.resultMsg;
        
        if (resultCode === "400") {
          openConfirmModal({
            title: "제출 실패",
            content: resultMsg || "제출에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          messageApi.success("제출되었습니다.");
          // 성공 시 메인화면으로 이동
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        // 제출 실패 modal 창 표시
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 기능 : handleCancelSubmit
   * 설명 : 제출취소 버튼 클릭 시 실행되는 함수
   * param : None
   * return : None
   **/
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  /**
   * 기능 : performCancelSubmit
   * 설명 : 실제 제출취소 로직 실행 (bizPlgSubYn='N'으로 저장)
   *        성공 시 "제출취소 되었습니다." 알림 후 인사평가 메인으로 이동
   *        실패 시 "[서버메시지] 때문에 제출취소가 안됐습니다." 알림
   **/
  const performCancelSubmit = () => {
    const requestData = {
      empNo: empNo,
      evlYear: evlYear,
      bizPlgSubYn: "N",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장 (저장/제출/취소 공통)
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const resultCode = data.resultCode;
        const resultMsg = data.resultMsg;
        
        if (resultCode === "400") {
          openConfirmModal({
            title: "제출취소 실패",
            content: resultMsg || "제출취소에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          messageApi.success("제출취소 되었습니다.");
          // 성공 시 메인화면으로 이동
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        // 제출취소 실패 modal 창 표시
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 기능 : handleSave
   * 설명 : 저장 버튼 클릭 시 실행되는 함수
   * param : None
   * return : None
   **/
  const handleSave = () => {
    openConfirmModal({
      title: "저장하시겠습니까?",
      content: "입력한 내용이 임시 저장됩니다.",
      okText: "저장",
      cancelText: "취소",
      onOk: () => {
        performSave();
      },
    });
  };

  /**
   * 기능 : performSave
   * 설명 : 실제 저장 로직 실행 (TEVL010 저장 + TEVL009 저장)
   **/
  const performSave = () => {
    // 날짜 형식 변환 함수 (dayjs -> YYYYMMDD)
    const formatDate = (date: any): string => {
      if (!date) return "";
      return dayjs(date).format("YYYYMMDD");
    };

    // 날짜 범위 분해 (YYYYMMDD-YYYYMMDD -> [YYYYMMDD, YYYYMMDD])
    const splitPeriod = (period: [any, any] | null): [string, string] => {
      if (!period || !period[0] || !period[1]) return ["", ""];
      return [
        dayjs(period[0]).format("YYYYMMDD"),
        dayjs(period[1]).format("YYYYMMDD"),
      ];
    };

    // SNO를 2자리 문자열로 변환
    const formatSno = (num: number): string => {
      return num.toString().padStart(2, "0");
    };

    // DTO 구조에 맞게 요청 데이터 생성
    const requestData: any = {
      empNo: empNo, // props에서 받은 값 사용
      evlYear: evlYear,
      // === TEVL010 (본인정보) - root 레벨 ===
      grdCd: grdCd,
      currPosStDt: formatDate(currentGradeStartDate),
      bizPlgStDt: splitPeriod(pledgePeriod)[0],
      bizPlgEdDt: splitPeriod(pledgePeriod)[1],
      // === TEVL009 (상세) ===
      list: [],
    };

    // 담당 직무 추가 (동적 hrEvlItmId 사용)
    tasks.forEach((task, index) => {
      const [stDt, edDt] = splitPeriod(task.period);
      requestData.list.push({
        hrEvlItmId: task.hrEvlItmId || "",
        sno: formatSno(index + 1),
        taskNm: task.name,
        goalCntn: task.goal,
        weightRt: task.weight ? parseInt(task.weight) : 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 팀기여 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq에서 가져옴)
    teamContribution.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      // SNO: itmSeq 우선, 없으면 item.sno 사용
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      requestData.list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 자기개발 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq에서 가져옴)
    selfDevelopment.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      // SNO: itmSeq 우선, 없으면 item.sno 사용
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      requestData.list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // API 호출
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const resultCode = data.resultCode;
        const resultMsg = data.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "저장 실패",
            content: resultMsg || "저장에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          messageApi.success("저장되었습니다.");
          // 저장 후 재조회 (조회 메시지 숨김)
          loadPledgeData(true);
        }
      },
      error: (errorMsg: string) => {
        messageApi.error(errorMsg || "저장에 실패했습니다.");
      },
    });
  };

  /**
   * 기능 : updateEtcRow
   * 설명 : 기타 서약 부문의 특정 항목을 업데이트
   **/
  const updateEtcRow = (
    type: "team" | "self",
    key: string,
    field: "content" | "period",
    value: any,
  ) => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      if (type === "team") {
        setTeamContribution((prev) =>
          prev.map((item) =>
            item.key === key ? { ...item, [field]: value } : item,
          ),
        );
      } else {
        setSelfDevelopment((prev) =>
          prev.map((item) =>
            item.key === key ? { ...item, [field]: value } : item,
          ),
        );
      }
    });
  };

  /**
   * 기능 : totalWeight
   * 설명 : 현재 비중 합계 값을 저장하는 변수 (합계 표시 및 유효성 검사에 사용)
   **/
  const totalWeight = getTotalWeight();
  if (accessBlocked) {
    return <>{contextHolder}</>;
  }

  return (
    <>
      {contextHolder}

      <div style={{ maxWidth: 1600, margin: "0 auto", padding: 24 }}>
        {isDataLoading ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Spin size="large" tip="데이터를 조회 중입니다..." />
          </div>
        ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Header */}
          <Space
            direction="vertical"
            align="center"
            style={{ width: "100%" }}
            size="small"
          >
            <Text type="secondary">{evlYear} 년도</Text>
            <h1 style={{ margin: 0, fontWeight: "bold" }}>업무서약서</h1>
            <Divider style={{ margin: "12px 0" }} />
          </Space>

          {/* 본인 정보 */}
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <h3 style={{ margin: 0, fontWeight: "bold" }}>본인 정보</h3>
            <div
              style={{
                border: "2px solid #d9d9d9",
                borderRadius: "8px",
                padding: 16,
                backgroundColor: "#fff",
              }}
            >
              <Descriptions
                bordered
                size="small"
                column={3}
                styles={{
                  label: {
                    backgroundColor: "#fafafa",
                    fontWeight: 500,
                    width: "12%",
                    textAlign: "center",
                  },
                  content: { backgroundColor: "#fff", textAlign: "center" },
                }}
              >
                <Descriptions.Item label="이름">
                  <Input
                    readOnly
                    value={userInfo.userNm}
                    variant="borderless"
                    style={{ cursor: "default" }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="직군">
                  <div ref={grdSelectRef}>
                    <Select
                      value={grdCd || undefined}
                      onChange={(value) => {
                        setGrdCd(value);
                        const selected = grdCdOptions.find(
                          (opt) => opt.cd === value,
                        );
                        setUserInfo((prev) => ({
                          ...prev,
                          grdNm: selected?.cdNm || "",
                        }));
                      }}
                      placeholder="선택"
                      style={{ width: "100%" }}
                      disabled={!isEditable}
                    >
                      {grdCdOptions.map((opt) => (
                        <Select.Option key={opt.cd} value={opt.cd}>
                          {opt.cdNm}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="직위">
                  <Input
                    readOnly
                    value={userInfo.dutyNm}
                    variant="borderless"
                    style={{ cursor: "default" }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="직급">
                  <Input
                    readOnly
                    value={userInfo.postNm}
                    variant="borderless"
                    style={{ cursor: "default" }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="사업부">
                  <Input
                    readOnly
                    value={userInfo.upperDeptNm}
                    variant="borderless"
                    style={{ cursor: "default" }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="소속팀">
                  <Input
                    readOnly
                    value={userInfo.deptNm}
                    variant="borderless"
                    style={{ cursor: "default" }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="현직급시작일자">
                  <div ref={currentGradeStartDateRef}>
                    <DatePicker
                      value={currentGradeStartDate}
                      onChange={(date) => setCurrentGradeStartDate(date)}
                      disabledDate={disabledFutureDate}
                      style={{ width: "100%" }}
                      disabled={!isEditable}
                    />
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="업무서약기간" span={2}>
                  <div ref={pledgePeriodRef}>
                    <RangePicker
                      value={pledgePeriod}
                      onChange={(dates) => setPledgePeriod(dates as [any, any])}
                      style={{ width: "100%" }}
                      disabled={!isEditable}
                    />
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </Space>

          {/* 담당 직무 */}
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontWeight: "bold" }}>담당 직무 부문</h3>
              <Button
                size="small"
                icon={textAreaExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => setTextAreaExpanded((prev) => !prev)}
              >
                {textAreaExpanded ? "전체 접기" : "전체 펼치기"}
              </Button>
            </div>
            <div
              style={{
                border: "2px solid #d9d9d9",
                borderRadius: "8px",
                padding: 16,
                backgroundColor: "#fff",
              }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div style={{ textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addTask}
                    disabled={!isEditable}
                  >
                    업무 추가
                  </Button>
                </div>
                <div ref={tasksTableRef}>
                  <Table
                    dataSource={tasks}
                    rowKey="id"
                    pagination={false}
                    bordered
                    size="small"
                    onRow={(record, index) =>
                      ({
                        "data-task-index": index,
                      }) as any
                    }
                    columns={[
                      {
                        title: "업무명",
                        dataIndex: "name",
                        key: "name",
                        width: "20%",
                        render: (text: string, record: TaskRow) => (
                          <CountTextArea
                            rowKey={`name-${record.id}-${text || ""}`}
                            rows={2}
                            defaultValue={text}
                            maxLength={100}
                            onSave={(value) =>
                              updateTask(record.id, "name", value)
                            }
                            disabled={!isEditable}
                            expanded={textAreaExpanded}
                          />
                        ),
                      },
                      {
                        title: "비중 (%)",
                        dataIndex: "weight",
                        key: "weight",
                        width: "5%",
                        align: "center",
                        render: (_: any, record: TaskRow) => (
                          <Input
                            value={record.weight}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                if (val === "" || parseInt(val) <= 100) {
                                  updateTask(record.id, "weight", val === "" ? "" : String(parseInt(val)));
                                }
                              }
                            }}
                            disabled={!isEditable}
                            style={{ textAlign: "center" }}
                          />
                        ),
                      },
                      {
                        title: "업무목표",
                        dataIndex: "goal",
                        key: "goal",
                        width: "33%",
                        render: (text: string, record: TaskRow) => (
                          <CountTextArea
                            rowKey={`goal-${record.id}-${text || ""}`}
                            rows={2}
                            defaultValue={text}
                            maxLength={500}
                            onSave={(value) =>
                              updateTask(record.id, "goal", value)
                            }
                            disabled={!isEditable}
                            expanded={textAreaExpanded}
                          />
                        ),
                      },
                      {
                        title: "기간",
                        key: "period",
                        width: "20%",
                        align: "center",
                        render: (_: any, record: TaskRow) => (
                          <RangePicker
                            value={record.period}
                            onChange={(dates) => {
                              setTasks((prev) =>
                                prev.map((t) =>
                                  t.id === record.id
                                    ? { ...t, period: dates }
                                    : t,
                                ),
                              );
                            }}
                            style={{ width: "100%" }}
                            disabled={!isEditable}
                          />
                        ),
                      },
                      {
                        title: "삭제",
                        key: "action",
                        width: "6%",
                        align: "center",
                        render: (_: any, record: TaskRow) =>
                          tasks.length > 1 && isEditable ? (
                            <Button 
                              danger 
                              size="small"
                              onClick={() => {
                                Modal.confirm({
                                  title: "정말로 이 행을 삭제하시겠습니까?",
                                  okText: "확인",
                                  cancelText: "취소",
                                  onOk: () => {
                                    removeTask(record.id);
                                  },
                                  onCancel: () => {
                                    console.log('삭제가 취소되었습니다.');
                                  }
                                });
                              }}
                            >
                              삭제
                            </Button>
                          ) : null,
                      },
                    ]}
                    footer={() => (
                      <div
                        style={{
                          textAlign: "right",
                          fontWeight: 500,
                        }}
                      >
                        <Space size="small">
                          <Text strong>합계: {totalWeight}%</Text>
                          {totalWeight !== 100 && (
                            <Text type="danger">(100%가 되어야 제출 가능)</Text>
                          )}
                        </Space>
                      </div>
                    )}
                  />
                </div>
              </Space>
            </div>
          </Space>

          {/* 기타 서약 부문 */}
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <h3 style={{ margin: 0, fontWeight: "bold" }}>기타 서약 부문</h3>
            <div
              style={{
                border: "2px solid #d9d9d9",
                borderRadius: "8px",
                padding: 16,
                backgroundColor: "#fff",
              }}
            >
              {/* 팀기여 부문 */}
              <Space
                direction="vertical"
                size={4}
                style={{ width: "100%", marginBottom: 12 }}
              >
                <h4
                  style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold" }}
                >
                  팀기여 부문
                </h4>
                <div ref={teamContributionTableRef}>
                  <Table
                    dataSource={teamContribution}
                    pagination={false}
                    bordered
                    size="small"
                    onRow={(record, index) =>
                      ({
                        "data-team-index": index,
                      }) as any
                    }
                    columns={[
                      {
                        title: "구분",
                        dataIndex: "category",
                        key: "category",
                        width: "10%",
                        align: "center",
                      },
                      {
                        title: <div style={{ textAlign: "center" }}>내용</div>,
                        dataIndex: "content",
                        key: "content",
                        width: "65%",
                        render: (text: string, record: EtcRow) => (
                          <CountTextArea
                            rowKey={`team-${record.key}-${text || ""}`}
                            rows={2}
                            defaultValue={text}
                            maxLength={500}
                            onSave={(value) =>
                              updateEtcRow(
                                "team",
                                record.key,
                                "content",
                                value,
                              )
                            }
                            disabled={!isEditable}
                            expanded={textAreaExpanded}
                          />
                        ),
                      },
                      {
                        title: <div style={{ textAlign: "center" }}>기간</div>,
                        dataIndex: "period",
                        key: "period",
                        width: "25%",
                        render: (period: [any, any] | null, record: EtcRow) => (
                          <RangePicker
                            value={period}
                            onChange={(dates) =>
                              updateEtcRow("team", record.key, "period", dates)
                            }
                            style={{ width: "100%" }}
                            disabled={!isEditable}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              </Space>

              {/* 자기개발 부문 */}
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <h4
                  style={{ marginTop: 0, marginBottom: 0, fontWeight: "bold" }}
                >
                  자기개발 부문
                </h4>
                <div ref={selfDevelopmentTableRef}>
                  <Table
                    dataSource={selfDevelopment}
                    pagination={false}
                    bordered
                    size="small"
                    onRow={(record, index) =>
                      ({
                        "data-self-index": index,
                      }) as any
                    }
                    columns={[
                      {
                        title: "구분",
                        dataIndex: "category",
                        key: "category",
                        width: "10%",
                        align: "center",
                      },
                      {
                        title: <div style={{ textAlign: "center" }}>내용</div>,
                        dataIndex: "content",
                        key: "content",
                        width: "65%",
                        render: (text: string, record: EtcRow) => (
                          <CountTextArea
                            rowKey={`self-${record.key}-${text || ""}`}
                            rows={2}
                            defaultValue={text}
                            maxLength={500}
                            onSave={(value) =>
                              updateEtcRow(
                                "self",
                                record.key,
                                "content",
                                value,
                              )
                            }
                            disabled={!isEditable}
                            expanded={textAreaExpanded}
                          />
                        ),
                      },
                      {
                        title: <div style={{ textAlign: "center" }}>기간</div>,
                        dataIndex: "period",
                        key: "period",
                        width: "25%",
                        render: (period: [any, any] | null, record: EtcRow) => (
                          <RangePicker
                            value={period}
                            onChange={(dates) =>
                              updateEtcRow("self", record.key, "period", dates)
                            }
                            style={{ width: "100%" }}
                            disabled={!isEditable}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              </Space>
            </div>
          </Space>

          {/* Footer - 미리보기/읽기전용 모드에서는 버튼 전체 숨김 */}
          {!isPreview && !isReadonly && (
          <Space style={{ width: "100%", justifyContent: "center" }}>
            {bizPlgSubYn !== "Y" && evlEnableYn === "Y" && (
              <>
                <Button size="large" onClick={handleSave} disabled={!isEditable}>
                  저장
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubmit}
                  disabled={!isEditable}
                >
                  제출
                </Button>
              </>
            )}
            {bizPlgSubYn === "Y" && isWithinDeadline && (
              <Button
                danger
                size="large"
                onClick={handleCancelSubmit}
              >
                제출취소
              </Button>
            )}
          </Space>
          )}
        </Space>
        )}
      </div>
    </>
  );
};

export default Evl0102m;
